import { useState, useEffect, useCallback } from 'react'
import JSZip from 'jszip'
import { fetchBoxtraxSyncLogs, type BoxtraxSyncLog } from '../lib/boxtrax/syncLogs'

interface SyncHistoryDialogProps {
  isOpen: boolean
  onClose: () => void
  jobId: string
  jobRef: string
}

function statusBadgeClass(status: number | null): string {
  if (status == null) return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
  if (status >= 200 && status < 300) return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
  return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
}

function syncStatusBadgeClass(status: string): string {
  if (status === 'success') return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
  if (status === 'partial') return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
  if (status === 'error') return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
  return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
}

// Pretty-printed JSON for both display and the ZIP export -- request/response
// are already parsed JS values (parsed server-side from BoxTop's JSON
// response, or a plain object for the request), never a raw string here.
function toPrettyJson(value: unknown): string {
  if (value == null) return 'null'
  return JSON.stringify(value, null, 2)
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={async () => {
        await navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      }}
      className="px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded transition-colors"
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
  )
}

function JsonBlock({ label, value }: { label: string; value: unknown }) {
  const text = toPrettyJson(value)
  return (
    <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">{label}</span>
        <CopyButton text={text} />
      </div>
      <pre className="p-3 text-xs font-mono text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-900 overflow-x-auto max-h-80 whitespace-pre">{text}</pre>
    </div>
  )
}

type ExpandedPanel = 'request' | 'response' | null

export default function SyncHistoryDialog({ isOpen, onClose, jobId, jobRef }: SyncHistoryDialogProps) {
  const [logs, setLogs] = useState<BoxtraxSyncLog[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Record<string, ExpandedPanel>>({})
  const [downloading, setDownloading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setLogs(await fetchBoxtraxSyncLogs(jobId))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load sync history')
    } finally {
      setLoading(false)
    }
  }, [jobId])

  useEffect(() => {
    if (isOpen) load()
  }, [isOpen, load])

  if (!isOpen) return null

  function togglePanel(logId: string, panel: ExpandedPanel) {
    setExpanded(prev => ({ ...prev, [logId]: prev[logId] === panel ? null : panel }))
  }

  async function handleDownloadZip() {
    setDownloading(true)
    try {
      const zip = new JSZip()
      logs.forEach((log, idx) => {
        // Zero-padded index prefix keeps files in chronological order once
        // extracted -- logs are already sorted newest-first for the table,
        // but a flat folder of files with only timestamps in the name sorts
        // inconsistently across OSes/zip tools.
        const seq = String(idx + 1).padStart(3, '0')
        const safeEndpoint = log.endpoint.replace(/[^a-zA-Z0-9_-]/g, '_')
        const base = `${seq}_${safeEndpoint}_${log.createdAt.replace(/[:.]/g, '-')}`
        zip.file(`${base}_request.json`, toPrettyJson(log.requestJson))
        zip.file(`${base}_response.json`, toPrettyJson({
          http_status: log.httpStatus,
          error_msg: log.errorMsg,
          response: log.responseJson,
        }))
      })
      const blob = await zip.generateAsync({ type: 'blob' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `boxtop_sync_${jobRef}_${new Date().toISOString().slice(0, 10)}.zip`
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="w-full max-w-4xl max-h-[85vh] bg-white dark:bg-gray-800 rounded-xl shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">BoxTop Sync History</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{jobRef}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownloadZip}
              disabled={downloading || logs.length === 0}
              className="px-3 py-1.5 text-sm font-medium bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
            >
              {downloading ? 'Preparing...' : 'Download all as ZIP'}
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 px-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading...</div>
          ) : error ? (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">{error}</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">No BoxTop sync attempts recorded for this job yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="text-left py-1.5 pr-3 font-medium">Timestamp</th>
                  <th className="text-left py-1.5 pr-3 font-medium">Endpoint</th>
                  <th className="text-left py-1.5 pr-3 font-medium">Status Code</th>
                  <th className="text-left py-1.5 pr-3 font-medium">Result</th>
                  <th className="text-left py-1.5 pr-3 font-medium">Initiated By</th>
                  <th className="text-left py-1.5 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {logs.map(log => (
                  <tr key={log.id}>
                    <td colSpan={6} className="py-2">
                      <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_1.5fr] gap-3 items-center">
                        <span className="text-gray-700 dark:text-gray-300 text-xs">{new Date(log.createdAt).toLocaleString()}</span>
                        <span className="text-gray-900 dark:text-white font-mono text-xs">{log.endpoint}</span>
                        <span>
                          {log.httpStatus != null ? (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusBadgeClass(log.httpStatus)}`}>{log.httpStatus}</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">No response</span>
                          )}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${syncStatusBadgeClass(log.status)}`}>{log.status}</span>
                        <span className="text-gray-700 dark:text-gray-300 text-xs">{log.initiatedBy || '—'}</span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => togglePanel(log.id, 'request')}
                            className={`px-2 py-1 text-xs font-medium rounded transition-colors ${expanded[log.id] === 'request' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                          >
                            View Request
                          </button>
                          <button
                            onClick={() => togglePanel(log.id, 'response')}
                            className={`px-2 py-1 text-xs font-medium rounded transition-colors ${expanded[log.id] === 'response' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                          >
                            View Response
                          </button>
                        </div>
                      </div>
                      {expanded[log.id] === 'request' && <JsonBlock label="Request JSON" value={log.requestJson} />}
                      {expanded[log.id] === 'response' && (
                        <JsonBlock
                          label="Response JSON"
                          value={log.errorMsg ? { http_status: log.httpStatus, error_msg: log.errorMsg, response: log.responseJson } : log.responseJson}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}
